import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  Button,
  DatePicker,
  DatePickerInput,
  FileUploader,
  InlineNotification,
  Loading,
  TextArea,
  TextInput,
  Tile,
} from "@carbon/react";
import AuthLayout from "../../components/carbon/AuthLayout/AuthLayout";
import {
  useGetDeliveryByToken,
  useConfirmDelivery,
  useUploadRemitoFileByToken,
} from "../../hooks/useDeliveries";
import { formatDate } from "../../utils";
import { translateApiError } from "../../utils/translateApiError";
import { DeliveryConfirmationResponse } from "../../types";
import "./deliveryConfirmation.scss";

const COMMENT_MAX = 255;

/** Local YYYY-MM-DD (no timezone shift, unlike toISOString). */
const toIsoDate = (d?: Date): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
        d.getDate()
      ).padStart(2, "0")}`
    : "";

/**
 * Anonymous delivery confirmation (`/deliveries/token/:token`), rebuilt on Carbon + `AuthLayout`.
 * A warehouse recipient opens the tokenised link, sees the delivery date/time + material lines, and
 * confirms with a reception date, remittance number and comment. Consumes the LEAN token DTO
 * (Unauthenticated-flow Task 4 — no buyer/company/pricing graph). The "already confirmed" banner is
 * gated on `delivery.tokenUsed || isConfirmed`, so a reopened/used link shows the banner, not the form.
 */
const DeliveryConfirmation: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [comment, setComment] = useState("");
  const [receptionDate, setReceptionDate] = useState("");
  const [remittanceNumber, setRemittanceNumber] = useState("");
  const [remittanceFile, setRemittanceFile] = useState("");
  const [uploadError, setUploadError] = useState(false);
  const [isConfirmed, setIsConfirmed] = useState(false);
  const [deliveryData, setDeliveryData] =
    useState<DeliveryConfirmationResponse | null>(null);

  const { mutate: uploadRemitoFile, isPending: isUploading } =
    useUploadRemitoFileByToken(
      (data) => {
        setRemittanceFile(data.url);
        setUploadError(false);
      },
      () => {
        setRemittanceFile("");
        setUploadError(true);
      }
    );

  const { mutate: getDeliveryByToken, isPending: isLoading } =
    useGetDeliveryByToken((data) => {
      setDeliveryData(data);
    });

  const {
    mutate: confirmDelivery,
    isPending,
    isError,
    error: confirmError,
  } = useConfirmDelivery(() => {
    setIsConfirmed(true);
  });

  useEffect(() => {
    if (token) {
      getDeliveryByToken({ token });
    }
  }, [token, getDeliveryByToken]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    confirmDelivery({
      token,
      receiverComment: comment,
      receptionDate,
      remittanceNumber,
      remittanceFile: remittanceFile || undefined,
    });
  };

  if (isLoading || isPending) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  const alreadyConfirmed = isConfirmed || deliveryData?.delivery?.tokenUsed;

  return (
    <AuthLayout>
      {deliveryData && (
        <div className="delivery-confirmation__summary">
          <h2 className="delivery-confirmation__heading">
            {t("deliveries.detail.deliveryDateTime")}
          </h2>
          <p className="delivery-confirmation__meta">
            {t("deliveries.form.deliveryDate")}:{" "}
            {formatDate(deliveryData.delivery.deliveryDate)}
          </p>
          <p className="delivery-confirmation__meta">
            {t("deliveries.form.deliveryTime")}:{" "}
            {deliveryData.delivery.deliveryTime}
          </p>

          <h3 className="delivery-confirmation__subheading">
            {t("materials.title")}
          </h3>
          <ul className="delivery-confirmation__materials">
            {deliveryData.selectedOffers.map((offer) => (
              <li key={offer.id} className="delivery-confirmation__material">
                <span className="delivery-confirmation__material-name">
                  {offer.order?.material?.globalMaterialName}
                </span>
                <span>
                  {offer.order?.quantity} {offer.order?.measurementUnit}
                </span>
              </li>
            ))}
          </ul>

          {deliveryData.delivery.receiverComment && (
            <>
              <h3 className="delivery-confirmation__subheading">
                {t("deliveries.form.comment")}
              </h3>
              <p className="delivery-confirmation__meta">
                {deliveryData.delivery.receiverComment}
              </p>
            </>
          )}
        </div>
      )}

      {isError ? (
        <InlineNotification
          kind="error"
          lowContrast
          hideCloseButton
          title={t("newMaterialForm.error")}
          subtitle={translateApiError(confirmError)}
        />
      ) : alreadyConfirmed ? (
        <Tile className="delivery-confirmation__success">
          {t("deliveries.receiverComment.success")}
        </Tile>
      ) : (
        <form onSubmit={handleSubmit} className="delivery-confirmation__form">
          <DatePicker
            datePickerType="single"
            dateFormat="Y-m-d"
            value={receptionDate}
            onChange={(dates: Date[]) => setReceptionDate(toIsoDate(dates[0]))}
          >
            <DatePickerInput
              id="receptionDate"
              labelText={t("deliveries.form.receptionDate")}
              placeholder="yyyy-mm-dd"
            />
          </DatePicker>

          <TextInput
            id="remittanceNumber"
            labelText={t("deliveries.form.remittanceNumber")}
            placeholder={t("deliveries.form.remittanceNumber")}
            maxLength={255}
            value={remittanceNumber}
            onChange={(e) => setRemittanceNumber(e.target.value)}
          />

          <FileUploader
            labelTitle={t("deliveries.form.remittanceFile")}
            labelDescription={t("deliveries.form.remittanceFileHint")}
            buttonLabel={t("deliveries.form.remittanceFileButton")}
            accept={[".pdf", ".jpg", ".jpeg", ".png"]}
            filenameStatus={isUploading ? "uploading" : "edit"}
            disabled={isUploading}
            onChange={(e: React.SyntheticEvent<HTMLElement>) => {
              const file = (e.target as HTMLInputElement).files?.[0];
              if (file && token) uploadRemitoFile({ token, file });
            }}
            onDelete={() => {
              setRemittanceFile("");
              setUploadError(false);
            }}
          />
          {uploadError && (
            <InlineNotification
              kind="error"
              lowContrast
              hideCloseButton
              title={t("deliveries.form.remittanceFileError")}
            />
          )}

          <TextArea
            id="comment"
            labelText={t("deliveries.form.comment")}
            placeholder={t("deliveries.receiverComment.placeholder")}
            rows={4}
            enableCounter
            maxCount={COMMENT_MAX}
            value={comment}
            onChange={(e) => {
              if (e.target.value.length > COMMENT_MAX) return;
              setComment(e.target.value);
            }}
          />

          <Button
            type="submit"
            className="delivery-confirmation__submit"
            disabled={isUploading}
          >
            {t("deliveries.receiverComment.confirmButton")}
          </Button>
        </form>
      )}
    </AuthLayout>
  );
};

export default DeliveryConfirmation;
